(function (root) {
  'use strict';
  const pairForVariant = (config, id) => (config.pairs || []).find(pair => pair.base === String(id) || pair.wrap === String(id));
  const targetVariant = (config, id, wrapped) => {
    const pair = pairForVariant(config, id);
    return pair ? pair[wrapped ? 'wrap' : 'base'] : null;
  };
  const optionIdentity = (config, options) => {
    for (let option = 0; option < options.length; option++) {
      const base = options[option].indexOf(config.baseValueId);
      const wrap = options[option].indexOf(config.wrapValueId);
      if (base >= 0 && wrap >= 0) return { option, base, wrap };
    }
    return null;
  };
  const api = { pairForVariant, targetVariant, optionIdentity };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (!root.customElements || root.customElements.get('byloro-gift-packaging')) return;
  root.customElements.define('byloro-gift-packaging', class extends HTMLElement {
    async connectedCallback() {
      this.form = document.getElementById(this.dataset.productFormId);
      this.checkbox = this.querySelector('[data-gift-packaging-checkbox]');
      this.status = this.querySelector('[data-gift-packaging-status]');
      if (!this.form || !this.checkbox) return;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.config = JSON.parse(this.querySelector('[data-packaging-config]').textContent);
      this.identities = JSON.parse(this.querySelector('[data-packaging-option-identities]').textContent);
      this.picker = document.querySelector('variant-picker[form="' + CSS.escape(this.dataset.productFormId) + '"]');
      this.form.addEventListener('submit', event => this.handleSubmit(event), { capture: true, signal });
      if (!this.config.baseValueId) return;
      this.checkbox.disabled = true;
      await root.customElements.whenDefined('variant-picker');
      for (let frame = 0; frame < 180 && this.isConnected && !this.picker?.product; frame++) {
        await new Promise(resolve => root.requestAnimationFrame(resolve));
      }
      if (!this.isConnected) return;
      const identity = optionIdentity(this.config, this.identities);
      const selector = identity && this.picker?.optionSelectors?.[identity.option];
      this.fieldset = selector?.closest('fieldset');
      if (!this.picker?.product || !this.fieldset) return this.fail();
      if (this.config.mode === 'variant') {
        const pair = pairForVariant(this.config, this.picker.selectedVariant?.id);
        if (!pair) return this.fail();
        this.checkbox.checked = pair.wrap === String(this.picker.selectedVariant.id);
        this.checkbox.addEventListener('change', () => this.syncVariant(), { signal });
        this.form.addEventListener('variant:change', () => queueMicrotask(() => {
          if (this.isConnected) this.syncVariant();
        }), { signal });
        root.addEventListener('popstate', () => {
          const id = new URL(root.location.href).searchParams.get('variant');
          const pair = pairForVariant(this.config, id);
          if (!pair) return;
          this.checkbox.checked = pair.wrap === id;
          this.picker.selectVariant(Number(id));
        }, { signal });
        this.ready = true;
      } else {
        const baseInput = selector.querySelectorAll('input[type="radio"]')[identity.base];
        if (!baseInput) return this.fail();
        baseInput.click();
        if (!baseInput.checked) return this.fail();
        this.ready = true;
      }
      this.fieldset.hidden = true;
      this.checkbox.disabled = false;
      this.dataset.packagingReady = 'true';
    }
    disconnectedCallback() { this.abort?.abort(); }
    fail() {
      this.ready = false;
      if (this.fieldset) this.fieldset.hidden = false;
      this.checkbox.disabled = true;
      this.status.textContent = this.dataset.errorMessage;
    }
    syncVariant() {
      const id = this.picker?.selectedVariant?.id;
      const target = targetVariant(this.config, id, this.checkbox.checked);
      if (!target) return this.fail();
      if (String(id) !== target) this.picker.selectVariant(Number(target));
      if (String(this.picker.selectedVariant?.id) !== target) return this.fail();
      this.status.textContent = '';
    }
    async handleSubmit(event) {
      if (this.config.baseValueId && !this.ready) {
        event.preventDefault(); event.stopImmediatePropagation(); return this.fail();
      }
      if (this.config.mode === 'variant') {
        this.syncVariant();
        if (!this.ready) { event.preventDefault(); event.stopImmediatePropagation(); }
        return;
      }
      if (!this.checkbox.checked) return;
      event.preventDefault(); event.stopImmediatePropagation();
      const variantInput = this.form.querySelector('[name="id"]');
      const submitButton = this.form.querySelector('[type="submit"]');
      if (!variantInput?.value) return;
      const properties = {};
      for (const [name, value] of new FormData(this.form).entries()) {
        const match = name.match(/^properties\[(.+)\]$/);
        if (match && typeof value === 'string' && value !== '') properties[match[1]] = value;
      }
      const quantity = Math.max(1, Number.parseInt(this.form.querySelector('[name="quantity"]')?.value || '1', 10) || 1);
      if (submitButton) { submitButton.disabled = true; submitButton.setAttribute('aria-busy', 'true'); }
      try {
        const response = await fetch(this.dataset.cartAddUrl, {
          method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: [{ id: Number(variantInput.value), quantity, properties }, { id: Number(this.dataset.giftVariantId), quantity: 1 }] })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || this.dataset.errorMessage);
        this.status.textContent = '';
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        this.form.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: result.items } }));
      } catch (error) { this.status.textContent = error.message || this.dataset.errorMessage; }
      finally { if (submitButton) { submitButton.disabled = false; submitButton.removeAttribute('aria-busy'); } }
    }
  });
})(typeof window !== 'undefined' ? window : {});
